import { useRef, useState } from "react";
import axios from "axios";
import { Image as ImageIcon, Upload, X, Loader2, AlertCircle, FolderOpen } from "lucide-react";
import MediaPicker from "@/components/admin/MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABS = (path) => (path?.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`);

/**
 * ImageField — paste a URL OR upload from your computer.
 * value: current image URL (or empty string)
 * onChange(url): called whenever URL changes (paste, upload success, or clear)
 * token: admin token for the upload endpoint
 */
export default function ImageField({ value, onChange, token, label = "Image URL", testid = "image-field" }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setError("");
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (PNG, JPG, WEBP, GIF, SVG).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Image too large (max 8 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name);
      const r = await axios.post(`${API}/admin/media/upload`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      const url = r.data.url || ABS(r.data.relative_url);
      onChange?.(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const previewSrc = value ? (value.startsWith("/api/") ? ABS(value) : value) : "";

  return (
    <div data-testid={testid}>
      <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
      <div className="flex gap-2 flex-wrap">
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Pick from library, upload, or paste a URL"
          className="flex-1 min-w-[180px] px-4 py-2.5 rounded-xl border border-cream-200 bg-white focus-gold focus:outline-none text-sm"
          data-testid={`${testid}-url`}
        />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid={`${testid}-pick`}
          className="px-4 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 shrink-0 bg-navy-deep text-cream hover:opacity-90"
        >
          <FolderOpen size={16} /> Library
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload`}
          className="btn-primary px-4 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 shrink-0 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Upload"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange?.("")}
            data-testid={`${testid}-clear`}
            aria-label="Remove image"
            className="px-3 py-2.5 rounded-xl border border-cream-200 hover:border-red-300 hover:text-red-600 text-sm shrink-0"
          >
            <X size={16} />
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-700 inline-flex items-center gap-1.5">
          <AlertCircle size={14} /> {error}
        </p>
      )}

      <div className="mt-3 rounded-lg border border-cream-200 bg-white overflow-hidden">
        {previewSrc ? (
          <img src={previewSrc} alt="" className="w-full max-h-48 object-cover" />
        ) : (
          <div className="h-32 flex items-center justify-center text-navy/40 bg-cream-50">
            <ImageIcon size={32} />
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-navy/55">Pick from your library, upload from your computer, or paste any URL · PNG, JPG, WEBP, GIF, SVG · up to 8 MB.</p>

      {pickerOpen && (
        <MediaPicker
          token={token}
          currentUrl={value}
          onPick={(url) => onChange?.(url)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
